import { v4 as uuid } from 'uuid';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getWishlistItemGtmData } from 'modules/gtm/modules/sections/wishlist/utils';
import {
  gtmAddToWishlist,
  gtmRemoveFromWishlist,
} from 'modules/gtm/modules/sections/wishlist/wishlist';
import { NotificationPayload } from 'modules/shop/notificationManager/types';
import { showNotificationWishlist } from 'modules/shop/notificationManager/wishlistNotifications';
import {
  addItemToWishlist,
  getWishlistById,
  removeFromWishlist,
} from 'commerce/scayle/wishlist';
import { WishlistFetchers, WishlistState } from './types';

interface ICreateWishlist {
  api: WishlistFetchers;
  showNotification: (payload: NotificationPayload) => void;
  gtm: {
    gtmAddToWishlist: typeof gtmAddToWishlist;
    gtmRemoveFromWishlist: typeof gtmRemoveFromWishlist;
  };
}

export const createWishlistStore = ({
  api,
  showNotification,
  gtm,
}: ICreateWishlist) =>
  create(
    persist<WishlistState>(
      (set, get) => ({
        total: 0,
        wishlist: null,
        wishlistId: null,
        loading: false,

        getWishlist: async () => {
          let { wishlistId } = get();
          set({ loading: true });
          if (!wishlistId) {
            wishlistId = uuid();
          }

          if (wishlistId) {
            const { response: wishlist } =
              await api.getWishlistById(wishlistId);

            set({
              wishlist,
              wishlistId,
              total: wishlist?.items?.length || 0,
              loading: false,
            });
          } else {
            console.error('No wishlist ID found');
            set({ loading: false });
          }
        },

        addToWishlist: async (variantId, message, pageType) => {
          const { wishlistId } = get();

          if (wishlistId) {
            const { response: wishlist } = await api.addItemToWishlist(
              wishlistId,
              {
                variantId,
              },
            );

            if (wishlist && message) {
              showNotification({
                name: message,
              });
            }

            const addedItem = getWishlistItemGtmData({
              wishlist,
              findItemCallback: (item) => item.variantId === variantId,
              pageType,
            });

            if (addedItem) {
              gtm.gtmAddToWishlist(addedItem);
            }

            set({ wishlist, total: wishlist?.items?.length || 0 });
          }
        },

        removeFromWishlist: async (itemKey, message, pageType) => {
          const { wishlistId, wishlist: currentWishlist } = get();
          set({ loading: true });

          if (wishlistId) {
            const { response: wishlist } = await api.removeFromWishlist(
              wishlistId,
              itemKey,
            );

            if (wishlist && message) {
              showNotification({
                name: message,
              });
            }

            const removedItem =
              currentWishlist &&
              getWishlistItemGtmData({
                wishlist: currentWishlist,
                findItemCallback: (item) => item.key === itemKey,
                pageType,
              });

            if (removedItem) {
              gtm.gtmRemoveFromWishlist(removedItem);
            }

            set({
              wishlist,
              total: wishlist?.items?.length || 0,
              loading: false,
            });
          }
        },
      }),
      {
        name: 'wishlist',
        storage: createJSONStorage(() => localStorage),
        /** @ts-ignore */
        partialize: ({ wishlistId }) => ({
          wishlistId,
        }),
      },
    ),
  );

/**
 * {wishlistId} is saved in localstorage with https://docs.pmnd.rs/zustand/integrations/persisting-store-data
 * */
export const useWishlist = createWishlistStore({
  api: {
    addItemToWishlist,
    getWishlistById,
    removeFromWishlist,
  },
  showNotification: showNotificationWishlist,
  gtm: { gtmAddToWishlist, gtmRemoveFromWishlist },
});
